import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppProvider, useApp } from "./contexts/AppContext";
import { Toaster } from "./components/ui/sonner";
import axios from "axios";

import AdminLayout from "./layouts/AdminLayout";
import TeacherLayout from "./layouts/TeacherLayout";
import DemoWalkthrough from "./components/DemoWalkthrough";
import ZoomControl from "./components/ZoomControl";
import LoginPage from "./pages/LoginPage";

import Dashboard from "./pages/Dashboard";
import Attendance from "./pages/Attendance";
import MealManager from "./pages/MealManager";
import Governance from "./pages/Governance";
import Health from "./pages/Health";
import LessonPlanner from "./pages/LessonPlanner";
import Incidents from "./pages/Incidents";
import IntegrationStatus from "./pages/IntegrationStatus";
import LMS from "./pages/LMS";
import Grades from "./pages/Grades";
import Communication from "./pages/Communication";
import CareerCounselling from "./pages/CareerCounselling";
import ResourceHub from "./pages/ResourceHub";
import AlumniNetwork from "./pages/AlumniNetwork";
import TabletTracking from "./pages/TabletTracking";
import UniformDistribution from "./pages/UniformDistribution";
import ThirdLanguage from "./pages/ThirdLanguage";
import MartyrsProgram from "./pages/MartyrsProgram";
import ManifestoTracker from "./pages/ManifestoTracker";
import Infrastructure from "./pages/Infrastructure";
import Facilities from "./pages/Facilities";
import PrePrimary from "./pages/PrePrimary";
import GreenBangladesh from "./pages/GreenBangladesh";
import TalentDevelopment from "./pages/TalentDevelopment";
import Scholarships from "./pages/Scholarships";
import Internships from "./pages/Internships";
import MadrasaTvet from "./pages/MadrasaTvet";
import ReformCommission from "./pages/ReformCommission";
import TeacherWelfare from "./pages/TeacherWelfare";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AppRoutes() {
  const { isAdmin, isTeacher, lang } = useApp();
  const [showDemo, setShowDemo] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);

  const startDemo = () => setShowDemo(true);

  const handleLogin = (data) => {
    // data is null for demo mode, or has access_token for real login
    setLoggedIn(true);
  };

  // Show landing/login page first
  if (!loggedIn) {
    return <LoginPage onLogin={handleLogin} lang={lang} />;
  }

  const adminBackendLink = (
    <div style={{ padding: 'var(--space-6)', borderTop: '1px solid var(--color-separator)', marginTop: 'var(--space-8)', textAlign: 'center' }}>
      <a href={`${process.env.REACT_APP_BACKEND_URL}/docs`} target="_blank" rel="noreferrer"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-label-tertiary)', textDecoration: 'none' }}>
        {lang === 'bn' ? 'অ্যাডমিন API প্যানেল (Swagger)' : 'Admin API Panel (Swagger)'} →
      </a>
    </div>
  );

  if (isTeacher) {
    return (
      <TeacherLayout onStartDemo={startDemo}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/attendance" element={<Attendance />} />
          <Route path="/meals" element={<MealManager />} />
          <Route path="/health" element={<Health />} />
          <Route path="/incidents" element={<Incidents />} />
          <Route path="/lesson-plans" element={<LessonPlanner />} />
          <Route path="/resource-hub" element={<ResourceHub />} />
          <Route path="/career" element={<CareerCounselling />} />
          <Route path="/alumni" element={<AlumniNetwork />} />
          <Route path="/tablets" element={<TabletTracking />} />
          <Route path="/uniforms" element={<UniformDistribution />} />
          <Route path="/third-language" element={<ThirdLanguage />} />
          <Route path="/martyrs" element={<MartyrsProgram />} />
          <Route path="/lms" element={<LMS />} />
          <Route path="/grades" element={<Grades />} />
          <Route path="/communication" element={<Communication />} />
          <Route path="/manifesto" element={<ManifestoTracker />} />
          <Route path="/infrastructure" element={<Infrastructure />} />
          <Route path="/facilities" element={<Facilities />} />
          <Route path="/pre-primary" element={<PrePrimary />} />
          <Route path="/green" element={<GreenBangladesh />} />
          <Route path="/talent" element={<TalentDevelopment />} />
          <Route path="/scholarships" element={<Scholarships />} />
          <Route path="/internships" element={<Internships />} />
          <Route path="/madrasa-tvet" element={<MadrasaTvet />} />
          <Route path="/reform" element={<ReformCommission />} />
          <Route path="/teacher-welfare" element={<TeacherWelfare />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        {adminBackendLink}
        {showDemo && <DemoWalkthrough onClose={() => setShowDemo(false)} />}
      </TeacherLayout>
    );
  }

  return (
    <AdminLayout onStartDemo={startDemo}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/governance" element={<Governance />} />
        <Route path="/attendance" element={<Attendance />} />
        <Route path="/meals" element={<MealManager />} />
        <Route path="/incidents" element={<Incidents />} />
        <Route path="/health" element={<Health />} />
        <Route path="/lesson-plans" element={<LessonPlanner />} />
        <Route path="/resource-hub" element={<ResourceHub />} />
        <Route path="/career" element={<CareerCounselling />} />
        <Route path="/alumni" element={<AlumniNetwork />} />
        <Route path="/tablets" element={<TabletTracking />} />
        <Route path="/uniforms" element={<UniformDistribution />} />
        <Route path="/third-language" element={<ThirdLanguage />} />
        <Route path="/martyrs" element={<MartyrsProgram />} />
        <Route path="/lms" element={<LMS />} />
        <Route path="/grades" element={<Grades />} />
        <Route path="/communication" element={<Communication />} />
        <Route path="/integrations" element={<IntegrationStatus />} />
        <Route path="/manifesto" element={<ManifestoTracker />} />
        <Route path="/infrastructure" element={<Infrastructure />} />
        <Route path="/facilities" element={<Facilities />} />
        <Route path="/pre-primary" element={<PrePrimary />} />
        <Route path="/green" element={<GreenBangladesh />} />
        <Route path="/talent" element={<TalentDevelopment />} />
        <Route path="/scholarships" element={<Scholarships />} />
        <Route path="/internships" element={<Internships />} />
        <Route path="/madrasa-tvet" element={<MadrasaTvet />} />
        <Route path="/reform" element={<ReformCommission />} />
        <Route path="/teacher-welfare" element={<TeacherWelfare />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      {adminBackendLink}
      {showDemo && <DemoWalkthrough onClose={() => setShowDemo(false)} />}
    </AdminLayout>
  );
}

function AppInit({ children }) {
  const { setCurrentSchool, setCurrentTeacher } = useApp();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    async function init() {
      try {
        const [schoolsRes, teachersRes] = await Promise.all([
          axios.get(`${API}/schools`),
          axios.get(`${API}/teachers`)
        ]);
        if (schoolsRes.data.length > 0) setCurrentSchool(schoolsRes.data[0]);
        if (teachersRes.data.length > 0) {
          const teacher = teachersRes.data.find(t => t.role === 'teacher') || teachersRes.data[0];
          setCurrentTeacher(teacher);
        }
      } catch (e) { /* Init error handled gracefully */ }
      setReady(true);
    }
    init();
  }, [setCurrentSchool, setCurrentTeacher]);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-background)' }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#006A4E', borderTopColor: 'transparent' }} />
          <p style={{ fontSize: 16, color: 'var(--color-label-secondary)' }}>Shiksha BD loading...</p>
        </div>
      </div>
    );
  }
  return children;
}

import ErrorBoundary from "./components/ErrorBoundary";

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <AppInit>
            <AppRoutes />
          </AppInit>
        </ErrorBoundary>
        <Toaster position="top-center" />
        <ZoomControl />
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
